import '../i18n';
import {useTranslation} from 'react-i18next';
import React, { useCallback, useState, useEffect, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { AuthContext } from '../util/context';

function MyDropzone({ title, children, filesSelected, disabled, dropTargets = [], primaryDropTarget = null }) {
    const {t: lnT} = useTranslation();

    const [files, setFiles] = useState([]);
    const [dropzoneError, setDropzoneError] = useState(null);
    const [activeDropTarget, setActiveDropTarget] = useState(null);
    const { state: { selectedFolder } = {} } = useContext(AuthContext) || {};

    const onDrop = useCallback(async (acceptedFiles, _rejectedFiles, event) => {
        if (disabled) {
            return;
        }

        if (!Array.isArray(acceptedFiles) || acceptedFiles.length === 0) {
            setDropzoneError(lnT('common:noValidFilesProvided'));
            return;
        }

        try {
            setDropzoneError(null);
            const detectedDropTarget = event?.target?.closest?.('[data-dropzone-target]')?.getAttribute?.('data-dropzone-target') || null;
            let nextDropTarget = detectedDropTarget || activeDropTarget || primaryDropTarget?.id || null;

            if (!nextDropTarget && typeof document !== 'undefined') {
                const clientX = Number(event?.clientX);
                const clientY = Number(event?.clientY);

                if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
                    const pointerTarget = document
                        .elementFromPoint(clientX, clientY)
                        ?.closest?.('[data-dropzone-target]');
                    nextDropTarget =
                        String(pointerTarget?.getAttribute?.('data-dropzone-target') || '').trim() || null;
                }
            }

            const processedFiles = await Promise.all(
                acceptedFiles.map(async (file) => {
                    try {
                        let preview;
                        try {
                            preview = URL.createObjectURL(file);
                        } catch (_) {
                            preview = null;
                        }

                        if (preview) {
                            try {
                                file.preview = preview;
                            } catch (_) {
                                Object.defineProperty(file, 'preview', {
                                    value: preview,
                                    writable: true,
                                    configurable: true
                                });
                            }
                        }

                        return file;
                    } catch (error) {
                        console.error('Error processing file:', error);
                        throw new Error(`Failed to process file ${file?.name || 'unknown'}: ${error.message}`);
                    }
                })
            );

            const validFiles = processedFiles.filter(Boolean);

            if (validFiles.length > 0) {
                setFiles(validFiles);
                if (filesSelected) {
                    try {
                        await filesSelected(validFiles, nextDropTarget);
                    } catch (error) {
                        console.error('Error notifying parent:', error);
                        setDropzoneError(lnT('common:errorNotifyingParentComponent'));
                    }
                }
            } else {
                setDropzoneError(lnT('common:noFilesWereSuccessfullyProcessed'));
            }
        } catch (error) {
            console.error('Drop error:', error);
            setDropzoneError(lnT('common:dropError', { value1: String(error.message) }));
        } finally {
            setActiveDropTarget(null);
        }
    }, [lnT, activeDropTarget, disabled, filesSelected, primaryDropTarget?.id]);

    const onDragEnter = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setActiveDropTarget(primaryDropTarget?.id || null);
    }, [lnT, primaryDropTarget?.id]);

    const onDragLeave = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget?.contains?.(event.relatedTarget)) {
            setActiveDropTarget(null);
        }
    }, [lnT]);

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        const nextDropTarget = event.target?.closest?.('[data-dropzone-target]')?.getAttribute?.('data-dropzone-target') || null;
        const resolvedDropTarget = nextDropTarget || primaryDropTarget?.id || null;
        setActiveDropTarget(previous => (previous === resolvedDropTarget ? previous : resolvedDropTarget));
    }, [lnT, primaryDropTarget?.id]);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        onDragEnter,
        onDragLeave,
        onDragOver,
        disabled,
        noClick: true,
        multiple: true,
        useFsAccessApi: false,
        preventDropOnDocument: true,
        noKeyboard: false
    });

    useEffect(() => {
        return () => {
            files.forEach(file => {
                if (file.preview && file.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, [files]);



    const renderTitle = () => {
        if (title) {
            return (
                <div style={{ color: 'white' }}>
                    <div style={{ opacity: 0.8 }}>{title}</div>
                </div>
            );
        } else {
            return (
                <div style={{ color: 'white' }}>
                    <div style={{ opacity: 0.8, marginBottom: 4 }}>{lnT('common:dropFilesHereToSendThemTo')}</div>
                    <div>{selectedFolder?.title}</div>
                </div>
            );
        }
    };

    const renderDropTargetsOverlay = () => {
        const hasSecondaryTargets = Array.isArray(dropTargets) && dropTargets.length > 0;
        if (!hasSecondaryTargets && !primaryDropTarget) {
            return null;
        }

        const overlayTitle = title === undefined ? lnT('common:dropFiles') : title;
        const isPrimaryActive = Boolean(primaryDropTarget) && activeDropTarget === primaryDropTarget.id;
        const overlayBorder = isPrimaryActive
            ? '1px solid rgba(255,255,255,0.32)'
            : '1px solid rgba(255,255,255,0.18)';

        return (
            <div
                style={{
                    background: 'rgba(19, 23, 24, 0.92)',
                    flex: 1,
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bottom: 8,
                    left: 8,
                    zIndex: 99999999,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 10,
                    padding: 14,
                    border: overlayBorder,
                    borderRadius: 10,
                    backdropFilter: 'blur(4px)'
                }}
            >
                {overlayTitle ? (
                    <div style={{ color: 'white', textAlign: 'center', opacity: 0.88 }}>
                        {overlayTitle}
                    </div>
                ) : null}
                {hasSecondaryTargets ? (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${dropTargets.length}, minmax(0, 1fr))`,
                            gap: 10
                        }}
                    >
                        {dropTargets.map(target => {
                            const isActive = activeDropTarget === target.id;
                            return (
                                <div
                                    key={target.id}
                                    data-dropzone-target={target.id}
                                    style={{
                                        minHeight: 96,
                                        borderRadius: 12,
                                        border: `1px solid ${isActive ? 'rgba(255,255,255,0.34)' : 'rgba(255,255,255,0.14)'}`,
                                        background: 'rgba(255,255,255,0.05)',
                                        color: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        padding: '12px 10px',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <div
                                        data-dropzone-target={target.id}
                                        style={{ fontWeight: 600, marginBottom: 4 }}
                                    >
                                        {target.label}
                                    </div>
                                    {target.description ? (
                                        <div
                                            data-dropzone-target={target.id}
                                            style={{ fontSize: 12, opacity: 0.72, lineHeight: 1.35 }}
                                        >
                                            {target.description}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                ) : null}
                {primaryDropTarget ? (
                    <div
                        data-dropzone-target={primaryDropTarget.id}
                        style={{
                            flex: 1,
                            minHeight: 132,
                            color: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '20px 18px 10px',
                            boxSizing: 'border-box'
                        }}
                    >
                        <div
                            data-dropzone-target={primaryDropTarget.id}
                            style={{ fontSize: 22, fontWeight: 700 }}
                        >
                            {primaryDropTarget.label}
                        </div>
                        {primaryDropTarget.description ? (
                            <div
                                data-dropzone-target={primaryDropTarget.id}
                                style={{ fontSize: 13, lineHeight: 1.45, opacity: 0.78, marginTop: 8, maxWidth: 360 }}
                            >
                                {primaryDropTarget.description}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <div
            {...getRootProps()}
            style={{
                    // pointerEvents: 'none',
                    // zIndex: 9999,
                    // position: 'absolute',
                    // top: 0,
                    // left: 0,
                    // right: 0,
                    // bottom: 0,
                    gridColumn: 3,
                    gridRow: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    flex: 1,
                    // backgroundColor: isDragActive
                    // 	? 'rgba(255,255,255,.3)'
                    // 	: 'transparent',
                    position: 'relative',
                    outline: 'none'
                    // flex: 1
                }}
        >
            <input {...getInputProps()} />
            {isDragActive && !disabled && (
                renderDropTargetsOverlay() || (
                    <div
                        style={{
                            background: 'rgba(19, 23, 24, 0.92)',
                            flex: 1,
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bottom: 8,
                            left: 8,
                            zIndex: 99999999,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 14,
                            border: '1px solid rgba(255,255,255,.16)',
                            borderRadius: 10,
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        <div
                            style={{
                                color: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                minHeight: 132,
                                minWidth: 'min(420px, 100%)',
                                padding: '20px 18px',
                                boxSizing: 'border-box'
                            }}
                        >
                            {renderTitle()}
                        </div>
                    </div>
                )
            )}
            {/* {!isDragActive && !disabled && (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                    Click or drag files here
                </div>
            )} */}
            {dropzoneError && (
                <div style={{ color: 'red', padding: '10px' }}>{lnT('common:error_8eecdde0')}{dropzoneError}
                </div>
            )}
            {children}
        </div>
    );
}

export default MyDropzone;
